import {CommonModule} from '@angular/common';
import {Component, computed, inject, OnInit, signal, viewChild, ChangeDetectionStrategy} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {ActivatedRoute} from '@angular/router';
import {Store} from '@ngrx/store';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeCardModule} from 'se-fe-card';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFeHeaderModule} from 'se-fe-header';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeLinkModule} from 'se-fe-link';
import {SeFeMenuModule, SeFeMenuOptions} from 'se-fe-menu';
import {SeFePageModule} from 'se-fe-page';
import {SeFeTabGroupModule} from 'se-fe-tabs';
import {environment} from '../../../environments/environment';
import {
  ProductTableComponent,
  ProductTableSelectionChange,
} from '../../common/components/product-table/product-table.component';
import {getTableRowId, ProductTableRow} from '../../common/components/product-table/product-table-row';
import {ProductQueryParams} from '../../models';
import {selectSelectedOrganizationId} from '../../state/router/router.selectors';
import {ChooseCatalogBundlesModalComponent} from './choose-bundles/choose-catalog-bundles-modal.component';
import {ChooseCatalogProductsModalComponent} from './choose-products/choose-catalog-products-modal.component';
import {EditCatalogModalComponent} from './edit-catalog/edit-catalog-modal.component';
import {CatalogDetailActions} from './store/catalog-detail.actions';
import {
  selectCatalogDetail,
  selectCatalogDetailError,
  selectCatalogDetailLoading,
  selectCatalogRegistrations,
  selectCatalogProductQuery,
  selectCatalogProducts,
  selectCatalogProductsError,
  selectCatalogProductsLoading,
  selectCatalogProductsPaginationOptions,
  selectCatalogProductSortBy,
  selectCatalogProductSortDirection,
  selectCatalogSelectedProductIds,
  selectCatalogBundleQuery,
  selectCatalogBundles,
  selectCatalogBundlesError,
  selectCatalogBundlesLoading,
  selectCatalogBundlesPaginationOptions,
  selectCatalogBundleSortBy,
  selectCatalogBundleSortDirection,
  selectCatalogSelectedBundleIds,
} from './store/catalog-detail.selectors';

type CatalogTab = 'products' | 'bundles';

@Component({
  selector: 'app-catalog-detail',
  templateUrl: './catalog-detail.component.html',
  styleUrls: ['./catalog-detail.component.scss'],
  host: {class: 'app-catalog-detail'},
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    ChooseCatalogBundlesModalComponent,
    ChooseCatalogProductsModalComponent,
    EditCatalogModalComponent,
    ProductTableComponent,
    SeFeAlertModule,
    SeFeButtonModule,
    SeFeCardModule,
    SeFeGridModule,
    SeFeHeaderModule,
    SeFeIconModule,
    SeFeLinkModule,
    SeFeMenuModule,
    SeFePageModule,
    SeFeTabGroupModule,
  ],
})
export class CatalogDetailComponent implements OnInit {
  readonly productChooser = viewChild(ChooseCatalogProductsModalComponent);
  readonly bundleChooser = viewChild(ChooseCatalogBundlesModalComponent);

  private route = inject(ActivatedRoute);
  private store = inject(Store);

  catalog$ = this.store.select(selectCatalogDetail);
  loading$ = this.store.select(selectCatalogDetailLoading);
  error$ = this.store.select(selectCatalogDetailError);
  registrations$ = this.store.select(selectCatalogRegistrations);
  products$ = this.store.select(selectCatalogProducts);
  productsLoading$ = this.store.select(selectCatalogProductsLoading);
  productsError$ = this.store.select(selectCatalogProductsError);
  productQuery$ = this.store.select(selectCatalogProductQuery);
  productSortBy$ = this.store.select(selectCatalogProductSortBy);
  productSortDirection$ = this.store.select(selectCatalogProductSortDirection);
  productPaginationOptions$ = this.store.select(selectCatalogProductsPaginationOptions);
  readonly selectedProductIds = toSignal(this.store.select(selectCatalogSelectedProductIds), {
    requireSync: true,
  });
  bundles$ = this.store.select(selectCatalogBundles);
  bundlesLoading$ = this.store.select(selectCatalogBundlesLoading);
  bundlesError$ = this.store.select(selectCatalogBundlesError);
  bundleQuery$ = this.store.select(selectCatalogBundleQuery);
  bundleSortBy$ = this.store.select(selectCatalogBundleSortBy);
  bundleSortDirection$ = this.store.select(selectCatalogBundleSortDirection);
  bundlePaginationOptions$ = this.store.select(selectCatalogBundlesPaginationOptions);
  readonly selectedBundleIds = toSignal(this.store.select(selectCatalogSelectedBundleIds), {
    requireSync: true,
  });
  activeCatalogTab = signal<CatalogTab>('products');
  backLink = '..';
  private readonly organizationId = toSignal(this.store.select(selectSelectedOrganizationId));
  readonly registrationListUrl = computed(() => {
    const organizationId = this.organizationId();
    return organizationId
      ? `${environment.registrationAdminUrl}/${encodeURIComponent(organizationId)}/survey/list`
      : null;
  });
  readonly productMenuOptions: SeFeMenuOptions = {
    name: 'Product Actions',
    sections: [
      {
        menuItems: [
          {
            text: 'Remove products',
            action: () => this.removeSelectedProductsFromCatalog(),
          },
        ],
      },
    ],
  };
  readonly bundleMenuOptions: SeFeMenuOptions = {
    name: 'Bundle Actions',
    sections: [
      {
        menuItems: [
          {
            text: 'Remove bundles',
            action: () => this.removeSelectedBundlesFromCatalog(),
          },
        ],
      },
    ],
  };

  ngOnInit(): void {
    const catalogId = this.route.snapshot.paramMap.get('catalogId')!;
    this.store.dispatch(CatalogDetailActions.load({catalogId}));
  }

  selectCatalogTab(tab: CatalogTab): void {
    this.activeCatalogTab.set(tab);
    if (tab === 'bundles') {
      this.store.dispatch(CatalogDetailActions.ensureBundlesLoaded());
    }
  }

  productQueryChanged(queryParams: ProductQueryParams): void {
    this.store.dispatch(CatalogDetailActions.productsQueryChanged({queryParams}));
  }

  productSelectionChanged({currentPageIds, selectedRows}: ProductTableSelectionChange): void {
    this.store.dispatch(
      CatalogDetailActions.productSelectionChanged({
        currentPageProductIds: currentPageIds,
        selectedProductIds: selectedRows.map(getTableRowId).filter((id): id is string => !!id),
      })
    );
  }

  removeProductFromCatalog(product: ProductTableRow): void {
    this.store.dispatch(CatalogDetailActions.removeProducts({productIds: [Number(getTableRowId(product)!)]}));
  }

  bundleQueryChanged(queryParams: ProductQueryParams): void {
    this.store.dispatch(CatalogDetailActions.bundlesQueryChanged({queryParams}));
  }

  bundleSelectionChanged({currentPageIds, selectedRows}: ProductTableSelectionChange): void {
    this.store.dispatch(
      CatalogDetailActions.bundleSelectionChanged({
        currentPageBundleIds: currentPageIds,
        selectedBundleIds: selectedRows.map(getTableRowId).filter((id): id is string => !!id),
      })
    );
  }

  removeBundleFromCatalog(bundle: ProductTableRow): void {
    this.store.dispatch(CatalogDetailActions.removeBundles({bundleIds: [Number(getTableRowId(bundle)!)]}));
  }

  private removeSelectedProductsFromCatalog(): void {
    const productIds = Array.from(this.selectedProductIds(), Number);
    if (productIds.length === 0) return;

    this.store.dispatch(CatalogDetailActions.removeProducts({productIds}));
  }

  private removeSelectedBundlesFromCatalog(): void {
    const bundleIds = Array.from(this.selectedBundleIds(), Number);
    if (bundleIds.length === 0) return;

    this.store.dispatch(CatalogDetailActions.removeBundles({bundleIds}));
  }
}
